/**
 * Service: Employee data access (CRUD).
 * Handles all Supabase interactions for employee records.
 * No UI, no React state — just async data operations.
 * All functions throw on error so callers can handle failures.
 */

import { supabase } from "@/integrations/supabase/client";
import { unwrapJoin } from "@/utils/unwrapJoin";
import {
  createEmployeeSchema,
  updateEmployeeSchema,
  searchQuerySchema,
} from "./validation";

export interface CreateEmployeeParams {
  firstName: string;
  lastName: string;
  email: string;
  roleTitle?: string;
  departmentId?: string | null;
  managerId?: string | null;
  hireDate?: string | null;
  phoneNumber?: string | null;
  workLocation?: string | null;
  organizationId: string;
}

export interface UpdateEmployeeParams {
  mandate?: string | null;
  key_tasks?: string[];
  contact_subject?: string | null;
  work_location?: string | null;
  phone_number?: string | null;
  specific_location?: string | null;
  preferred_language?: string | null;
  birthday?: string | null;
  role_title?: string | null;
  desired_position_title?: string | null;
}

/** Create a new employee record. */
export async function createEmployee(params: CreateEmployeeParams): Promise<void> {
  const validated = createEmployeeSchema.parse(params);
  const { error } = await supabase.from("employees").insert({
    first_name: validated.firstName,
    last_name: validated.lastName,
    email: validated.email,
    role_title: validated.roleTitle || null,
    department_id: validated.departmentId || null,
    manager_id: validated.managerId || null,
    hire_date: validated.hireDate || null,
    phone_number: validated.phoneNumber || null,
    work_location: validated.workLocation || null,
    organization_id: validated.organizationId,
  });
  if (error) throw new Error(error.message);
}

/** Update an existing employee profile. */
export async function updateEmployeeProfile(employeeId: string, data: UpdateEmployeeParams): Promise<void> {
  const validated = updateEmployeeSchema.parse(data);
  const { error } = await supabase
    .from("employees")
    .update(validated)
    .eq("id", employeeId);
  if (error) throw error;
}

/** Fetch departments list for dropdowns. */
export async function fetchDepartments(): Promise<Array<{ id: string; name: string }>> {
  const { data, error } = await supabase
    .from("departments")
    .select("id, name")
    .order("name");
  if (error) throw new Error(`Impossible de charger les départements : ${error.message}`);
  return data ?? [];
}

/** Fetch active employees for manager/combobox dropdowns. */
export async function fetchActiveEmployees(): Promise<Array<{ id: string; first_name: string; last_name: string }>> {
  const { data, error } = await supabase
    .from("employees")
    .select("id, first_name, last_name")
    .eq("is_active", true)
    .eq("is_demo", false)
    .order("first_name");
  if (error) throw new Error(`Impossible de charger les employés : ${error.message}`);
  return data ?? [];
}

// ─── Employee Lookups (shared across UI) ─────────────────────────────

export interface EmployeeSummary {
  id: string;
  first_name: string;
  last_name: string;
  role_title: string | null;
  avatar_url: string | null;
  organization_id?: string;
}

/** Fetch a single employee by ID (display fields). */
export async function fetchEmployeeById(employeeId: string): Promise<EmployeeSummary | null> {
  const { data, error } = await supabase
    .from("employees")
    .select("id, first_name, last_name, role_title, avatar_url, organization_id")
    .eq("id", employeeId)
    .single();
  if (error) throw new Error(`Impossible de charger l'employé : ${error.message}`);
  return data ?? null;
}

/** Fetch just the name of an employee (lightweight). */
export async function fetchEmployeeName(employeeId: string): Promise<{ first_name: string; last_name: string } | null> {
  const { data, error } = await supabase
    .from("employees")
    .select("first_name, last_name")
    .eq("id", employeeId)
    .single();
  if (error) throw new Error(`Impossible de charger le nom de l'employé : ${error.message}`);
  return data ?? null;
}

/** Search employees by name/email for autocomplete. */
export async function searchEmployeesByQuery(q: string, limit = 5): Promise<EmployeeSummary[]> {
  const validated = searchQuerySchema.parse(q);
  const { data, error } = await supabase
    .from("employees")
    .select("id, first_name, last_name, role_title, avatar_url")
    .or(`first_name.ilike.%${validated}%,last_name.ilike.%${validated}%,email.ilike.%${validated}%`)
    .eq("is_active", true)
    .eq("is_demo", false)
    .limit(limit);
  if (error) throw new Error(`Erreur de recherche employés : ${error.message}`);
  return data ?? [];
}

// ─── Competency Lookups (shared) ─────────────────────────────────────

export interface CompetencyWithGroup {
  id: string;
  name: string;
  description: string | null;
  groupId: string | null;
  groupName: string;
}

/** Fetch competencies by IDs with group name. */
export async function fetchCompetenciesByIds(ids: string[]): Promise<CompetencyWithGroup[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("competencies")
    .select("id, name, description, group_id, competency_groups(id, name)")
    .in("id", ids);
  if (error) throw new Error(`Impossible de charger les compétences : ${error.message}`);
  return (data ?? []).map((c) => {
    const group = unwrapJoin<{ id: string; name: string }>(c.competency_groups);
    return {
      id: c.id,
      name: c.name,
      description: c.description || "",
      groupId: group?.id ?? (c as { group_id?: string }).group_id ?? null,
      groupName: group?.name || "",
    };
  });
}

/** Employee with last meeting info for "no recent meeting" detection */
export interface EmployeeWithLastMeeting {
  id: string;
  first_name: string;
  last_name: string;
  role_title: string | null;
  avatar_url: string | null;
  department_name: string | null;
  work_location: string | null;
  last_meeting_date: string | null;
}

/**
 * Fetch employees who haven't had a completed evaluation in more than `months` months.
 */
export async function fetchEmployeesWithoutRecentMeeting(
  organizationId = "00000000-0000-0000-0000-000000000001",
  months = 3
): Promise<EmployeeWithLastMeeting[]> {
  // Get all active employees
  const { data: employees, error: empError } = await supabase
    .from("employees")
    .select("id, first_name, last_name, role_title, avatar_url, work_location, department_id, departments(name)")
    .eq("organization_id", organizationId)
    .eq("is_active", true)
    .eq("is_demo", false);

  if (empError) throw new Error(empError.message);
  if (!employees?.length) return [];

  // Get last completed evaluation per employee
  const { data: evals, error: evalError } = await supabase
    .from("evaluations")
    .select("employee_id, submitted_at")
    .eq("status", "completed")
    .order("submitted_at", { ascending: false });

  if (evalError) throw new Error(evalError.message);

  const lastMeetingMap = new Map<string, string>();
  (evals ?? []).forEach((ev) => {
    if (ev.employee_id && ev.submitted_at && !lastMeetingMap.has(ev.employee_id)) {
      lastMeetingMap.set(ev.employee_id, ev.submitted_at);
    }
  });

  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);

  return employees
    .filter((emp) => {
      const lastDate = lastMeetingMap.get(emp.id);
      if (!lastDate) return true; // never had a meeting
      return new Date(lastDate) < cutoff;
    })
    .map((emp) => {
      const dept = unwrapJoin<{ name: string }>(emp.departments);
      return {
        id: emp.id,
        first_name: emp.first_name,
        last_name: emp.last_name,
        role_title: emp.role_title,
        avatar_url: emp.avatar_url,
        department_name: dept?.name ?? null,
        work_location: emp.work_location,
        last_meeting_date: lastMeetingMap.get(emp.id) ?? null,
      };
    });
}
